import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { AlertTriangle, Video } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageLoadingState } from '@/components/layout/PageLoadingState';

import { useAuth } from '@/hooks/useAuthApi';
import { useOrganization } from '@/hooks/useOrganization';
import { type DomSessionRow } from '@/types/domSession';
import { useSessionsList } from '@/hooks/sessions/useSessionsList';
import { useSessionDetail } from '@/hooks/sessions/useSessionDetail';
import type { SalesSummary, SalesAnalyticsResponse } from '@/lib/api/sessions';
import SessionsFilters, { SessionsFilterState } from '@/components/sessions/Filters';
import { SessionsTableView } from '@/components/sessions/SessionsTableView';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { DataSurface } from '@/components/platform/DataSurface';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const initialFilters: SessionsFilterState = {
  search: '',
  qualificationStatus: [],
  bookedStatus: [],
  sessionStatus: [],
  sourceEnvironment: ['production'],
  dateRange: undefined,
};

// Convert SalesSummary to DomSessionRow for compatibility with existing components
function convertSalesSummaryToDomRow(summary: SalesSummary): DomSessionRow {
  return {
    id: summary.session_recording_id,
    sessionId: summary.session_recording_id,
    userEmail: summary.email || '',
    started_at: summary.started_at ?? summary.created_at,
    primary_intent: summary.primary_intent,
    summary: summary.summary_plaintext,
    story_summary_full: summary.summary_markdown,
    qualification_status: summary.qualification_status,
    updated_qualification_status: summary.updated_qualification_status,
    qualification_reason: summary.qualification_reason,
    booked_status: summary.booked_status,
    session_status: summary.session_status,
    audio_recording_url: summary.audio_recording_url ?? null,
    device: summary.device,
    browser: summary.browser,
    os: summary.os,
    geo_country: summary.geo_country,
    geo_region: summary.geo_region,
    geo_city: summary.geo_city,
    source_environment: summary.source_environment ?? 'production',
    duration_s: summary.duration_seconds ?? undefined,
    sales_summary: summary,
  };
}

// Convert SalesAnalyticsResponse to DomSessionRow
function convertSalesAnalyticsToDomRow(analytics: SalesAnalyticsResponse): DomSessionRow {
  const { sales_summary, conversation_timeline, topics, objections, transcripts, tool_calls } = analytics;

  return {
    id: sales_summary?.session_recording_id ?? '',
    sessionId: sales_summary?.session_recording_id ?? '',
    userEmail: sales_summary?.email || '',
    started_at: sales_summary?.started_at ?? sales_summary?.created_at,
    qualification_status: sales_summary?.qualification_status,
    updated_qualification_status: sales_summary?.updated_qualification_status,
    qualification_reason: sales_summary?.qualification_reason,
    booked_status: sales_summary?.booked_status,
    session_status: sales_summary?.session_status,
    primary_intent: sales_summary?.primary_intent,
    summary: sales_summary?.summary_plaintext,
    story_summary_full: sales_summary?.summary_markdown,
    audio_recording_url: sales_summary?.audio_recording_url ?? null,
    device: sales_summary?.device,
    browser: sales_summary?.browser,
    os: sales_summary?.os,
    geo_country: sales_summary?.geo_country,
    geo_region: sales_summary?.geo_region,
    geo_city: sales_summary?.geo_city,
    source_environment: sales_summary?.source_environment ?? 'production',
    // New sales analytics fields
    sales_summary,
    conversation_timeline,
    topics,
    objections,
    // Convert conversation timeline to legacy timeline format for compatibility
    timeline: conversation_timeline?.map((milestone, index) => ({
      id: milestone.id,
      sequence_order: milestone.sequence_order,
      action: milestone.description,
      description: milestone.description,
      step_label: milestone.milestone_type,
      timestamp_rel_s: milestone.timestamp_offset_s,
      features_mentioned: milestone.features_mentioned,
    })),
    // Include transcripts
    session_transcript: transcripts,
    transcripts: transcripts,
    // Include tool calls
    tool_calls: tool_calls || [],
  };
}


const SessionsPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<SessionsFilterState>(() => ({ ...initialFilters }));
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const initialSessionId = searchParams.get('sessionId');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(initialSessionId);
  const sessionIdRef = useRef<string | null>(initialSessionId);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const dateFilters = useMemo(() => {
    if (!filters.dateRange?.from) {
      return { date_from: undefined, date_to: undefined };
    }

    const date_from = filters.dateRange.from.toISOString().split('T')[0];
    const date_to = filters.dateRange.to
      ? filters.dateRange.to.toISOString().split('T')[0]
      : undefined;

    return { date_from, date_to };
  }, [filters.dateRange]);

  const sourceEnvironmentFilter = useMemo(() => {
    return filters.sourceEnvironment.length > 0
      ? filters.sourceEnvironment.join(',')
      : 'production,staging';
  }, [filters.sourceEnvironment]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    setPage(1);
  }, [filters.qualificationStatus, filters.bookedStatus, filters.sessionStatus, filters.sourceEnvironment, filters.dateRange, debouncedSearch]);

  // Fetch qualified sessions list
  const {
    sessions: sessionsList,
    total,
    offset: responseOffset,
    isLoading: isLoadingList,
    error: listError,
    prefetchSessionDetail,
  } = useSessionsList(currentOrganization?.id, {
    search: debouncedSearch || undefined,
    limit: pageSize,
    offset: (page - 1) * pageSize,
    qualification_status:
      filters.qualificationStatus.length > 0 ? filters.qualificationStatus.join(',') : undefined,
    booked_status: filters.bookedStatus.length > 0 ? filters.bookedStatus.join(',') : undefined,
    session_status: filters.sessionStatus.length > 0 ? filters.sessionStatus.join(',') : undefined,
    source_environment: sourceEnvironmentFilter,
    date_from: dateFilters.date_from,
    date_to: dateFilters.date_to,
    target_session_id: sessionIdRef.current ?? undefined,
    enabled: !!currentOrganization?.id,
  });

  // Fetch detailed data for selected session - on demand
  const {
    data: sessionDetail,
    isLoading: isLoadingDetail,
    error: detailError,
  } = useSessionDetail(selectedSessionId);

  // Convert sales summaries to DomSessionRow format for compatibility
  const sessions = useMemo(() => {
    const mapped = sessionsList.map(convertSalesSummaryToDomRow);
    // Sort by started_at descending (most recent first)
    return mapped.sort((a, b) => {
      const dateA = new Date(a.started_at || 0).getTime();
      const dateB = new Date(b.started_at || 0).getTime();
      return dateB - dateA;
    });
  }, [sessionsList]);

  useEffect(() => {
    if (!total) return;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    if (page > totalPages) {
      setPage(1);
    }
  }, [page, total]);

  // Sync page when backend overrides offset (e.g. target_session_id)
  useEffect(() => {
    const expectedOffset = (page - 1) * pageSize;
    if (responseOffset !== expectedOffset && !isLoadingList) {
      setPage(Math.floor(responseOffset / pageSize) + 1);
    }
  }, [responseOffset, isLoadingList]);

  const isLoading = isLoadingList;
  const isError = !!listError || !!detailError;
  const error = listError || detailError;

  const clearFilters = useCallback(() => {
    setFilters({ ...initialFilters });
  }, []);

  // Handle session selection based on results and URL params
  useEffect(() => {
    if (!sessions.length) {
      if (!sessionIdRef.current) {
        setSelectedSessionId(null);
      }
      return;
    }

    // Check if we need to select a session from URL (only on initial load or URL change)
    const pendingId = sessionIdRef.current;
    if (pendingId) {
      const target = sessions.find(
        (session) => session.id === pendingId || session.sessionId === pendingId
      );
      if (target) {
        setSelectedSessionId(target.id);
        sessionIdRef.current = null;
        return;
      }
    }

    // Check if current selection still exists in results
    const currentSelectionExists = selectedSessionId &&
      sessions.find((session) => session.id === selectedSessionId);

    if (!currentSelectionExists) {
      // Either no selection or selection no longer in results
      setSelectedSessionId(sessions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]); // Intentionally exclude selectedSessionId to avoid infinite loop

  const handleSelectSession = useCallback((session: DomSessionRow) => {
    setSelectedSessionId(session.id);
    // Don't update ref here since we're directly setting the selection
    const params = new URLSearchParams(searchParams);
    params.set('sessionId', session.id);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Prefetch on hover
  const handleSessionHover = useCallback((session: DomSessionRow) => {
    prefetchSessionDetail(session.id);
  }, [prefetchSessionDetail]);

  const handleResetFilters = () => {
    clearFilters();
  };

  // Clear URL param if no session is selected
  useEffect(() => {
    if (!selectedSessionId && searchParams.has('sessionId')) {
      const params = new URLSearchParams(searchParams);
      params.delete('sessionId');
      setSearchParams(params, { replace: true });
    }
  }, [selectedSessionId, searchParams, setSearchParams]);

  // Combine lightweight list data with detailed session data
  const selectedSession = useMemo(() => {
    if (!selectedSessionId) return null;

    // If we have detailed sales analytics data, use it
    if (sessionDetail) {
      return convertSalesAnalyticsToDomRow(sessionDetail);
    }

    // Otherwise fall back to lightweight list data
    const listItem = sessions.find(s => s.id === selectedSessionId);
    return listItem || null;
  }, [selectedSessionId, sessionDetail, sessions]);

  if (authLoading || orgLoading) {
    return <PageLoadingState />;
  }

  if (!user) {
    return <div className="p-4 text-center">Please log in to view sessions.</div>;
  }

  if (!currentOrganization?.id) {
    return <div className="p-4 text-center">Select an organization to view sessions.</div>;
  }

  return (
    <>
      <Helmet>
        <title>Sessions | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader icon={Video} title="Sessions" />

      {/* Filters */}
      <div className="border-b border-border/80 px-4 py-3">
        <SessionsFilters
          filters={filters}
          onFiltersChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))}
          onReset={handleResetFilters}
        />
      </div>

      {/* Main Content - Table View (Full Width) */}
      <div className="flex-1 overflow-hidden">
        <DataSurface className="h-full rounded-none border-0">
          <SessionsTableView
            sessions={sessions}
            selectedSession={selectedSession}
            isLoading={isLoading}
            onSessionSelect={handleSelectSession}
            onSessionHover={handleSessionHover}
            isLoadingDetail={isLoadingDetail}
            total={total}
            page={page}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS as unknown as number[]}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            onPageChange={setPage}
          />
        </DataSurface>

          {/* Error Alert - Overlay */}
          {isError && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Unable to load sessions</AlertTitle>
                <AlertDescription>
                  {error instanceof Error ? error.message : 'Failed to load sessions'}
                </AlertDescription>
              </Alert>
            </div>
          )}
      </div>
      </div>
    </>
  );
};

export default SessionsPage;
